import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { Observable, BehaviorSubject, of } from 'rxjs';
import { distinctUntilChanged, catchError, map, tap, finalize } from 'rxjs/operators';

import { JwtService } from './jwt.service';
import { LoaderService } from './loader.service';
import { MessageBoxService } from './message-box.service'; 

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private authSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public authState = this.authSubject.asObservable().pipe(distinctUntilChanged());

  // access token expiry 
  private accessTokenExpiry: number | null = null;

  constructor(
    private http: HttpClient,
    private jwtService: JwtService,
    private loaderService: LoaderService,
    private router: Router,
    private messageBox: MessageBoxService   
  ) {
    // restore expiry when app reloads
    const token = this.jwtService.getToken();
    if (token) {
      const payload = this.decodeJwt(token);
      if (payload && payload.exp) {
        this.accessTokenExpiry = payload.exp * 1000;
      }
    }
  }

  getAuth(): Observable<boolean> {
    return this.authSubject.asObservable();
  }

  isAuthenticated(): boolean {
    const token = this.jwtService.getToken();
    if (!token) {
      return false;
    }

    if (this.accessTokenExpiry) {
      return Date.now() < this.accessTokenExpiry;
    }

    return true;
  }

  getAccessToken(): string | null {
    return this.jwtService.getToken();
  }

  getAccessTokenExpiry(): number | null {
    return this.accessTokenExpiry;
  }

  setAuthFromToken(token: string): void {
    if (!token) {
      return;
    }

    this.jwtService.saveToken(token);

    const payload = this.decodeJwt(token);
    if (payload) {
      sessionStorage.setItem('userClaims', JSON.stringify(payload));

      if (payload.exp) {
        this.accessTokenExpiry = payload.exp * 1000;
      } else {
        this.accessTokenExpiry = null;
      }
    } else {
      this.accessTokenExpiry = null;
    }

    this.authSubject.next(false);
  }

  beginLogin(): void {
    window.location.href = '/api/auth/login';
  }

  // token refresh logic

  showAuthRefresh(): void {
    this.authSubject.next(true);
  }

  hideAuthRefresh(): void {
    this.authSubject.next(false);
  }

  hideLogoutAuthRefresh(): void {
    this.authSubject.next(false);
  }

  refreshToken(): Observable<boolean> {
    const token = this.jwtService.getToken();
    if (!token) {
      return of(false);
    }

    this.loaderService.showLoader();

    return this.http.post<{ token: string }>('/api/auth/RefreshToken', { token })
      .pipe(
        tap(resp => {
          if (resp && resp.token) {
            this.jwtService.saveToken(resp.token);

            const payload = this.decodeJwt(resp.token);
            if (payload && payload.exp) {
              this.accessTokenExpiry = payload.exp * 1000;
            } else {
              this.accessTokenExpiry = null;
            }

            this.hideAuthRefresh();
          }
        }),
        map(() => true),
        catchError(err => {
          console.error('Token refresh failed', err);
          this.logout(false);
          return of(false);
        }),
        finalize(() => {
          this.loaderService.hideLoader();
        })
      );
  }

  private clearClientAuth(): void {
    this.jwtService.destroyToken();

    this.accessTokenExpiry = null;

    this.hideLogoutAuthRefresh();
  }

  confirmLogout(showLoader: boolean = true): void {
    this.messageBox.confirm({
      title: 'Confirm Logout',
      message: 'Are you sure you want to logout?',
      warn: true
    })
    .subscribe(resp => {
      if (resp == 'yes') {           
        this.logout(showLoader);     
      } else {
      }
    });
  }
  

  logout(showLoader: boolean = true): void {
    if (showLoader) {
      this.loaderService.showLoader();
    }

    this.http.post('/api/auth/logout', {})
      .pipe(
        catchError(err => {
          console.error('Logout API error', err);
          return of(null);
        }),
        finalize(() => {
          if (showLoader) {
            this.loaderService.hideLoader();
          }
        })
      )
      .subscribe(() => {
        this.clearClientAuth();
        this.router.navigate(['/login']);
      });
  }

  // logout(showLoader: boolean = true): void {
  //   if (showLoader) {
  //     this.loaderService.showLoader();
  //   }
  
  //   // Clear JWT
  //  this.clearClientAuth();
 
  //  if (showLoader) {
  //     this.loaderService.hideLoader();
  //   }
  //   // Redirect to SAML logout
  //   window.location.href = '/api/auth/SamlLogout';
  // }

  private decodeJwt(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (e) {
      console.error('Failed to decode JWT', e);
      return null;
    }
  }
}
